import { useTranslation } from "react-i18next"
import { Check } from "lucide-react"
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import type { StylePreset } from "@/hooks/useStyles"
import type { StyleId } from "@/lib/constants"
import { localizeDescription, localizeName } from "@/lib/i18n-content"
import { StyleThumbnail } from "./StyleThumbnail"

interface StyleSelectionModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  styles: StylePreset[]
  selectedStyle: StyleId
  onStyleChange: (style: StyleId) => void
}

export function StyleSelectionModal({
  open,
  onOpenChange,
  styles,
  selectedStyle,
  onStyleChange,
}: StyleSelectionModalProps) {
  const { t } = useTranslation()
  const handleStyleSelect = (styleId: string) => {
    // Toggle: 이미 선택된 스타일을 다시 클릭하면 선택 해제
    if (selectedStyle === styleId) {
      onStyleChange(null)
    } else {
      onStyleChange(styleId)
    }
    onOpenChange(false)
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{t("generator.selectStyle")}</DialogTitle>
        </DialogHeader>
        <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-5 gap-3 mt-4">
          {styles.map((style) => (
            <StyleModalCard
              key={style.id}
              style={style}
              isSelected={selectedStyle === style.id}
              onSelect={() => handleStyleSelect(style.id)}
            />
          ))}
        </div>
      </DialogContent>
    </Dialog>
  )
}

interface StyleModalCardProps {
  style: StylePreset
  isSelected: boolean
  onSelect: () => void
}

function StyleModalCard({ style, isSelected, onSelect }: StyleModalCardProps) {
  // localizeName/localizeDescription 결과가 언어 변경 시 다시 렌더링되도록 구독
  useTranslation()
  return (
    <button
      onClick={onSelect}
      className={`relative rounded-lg border-2 overflow-hidden transition-all text-left max-w-[160px] ${
        isSelected
          ? "border-primary ring-2 ring-primary/20"
          : "border-slate-200 hover:border-slate-300"
      }`}
    >
      {/* Thumbnail */}
      <div className="w-full aspect-square bg-slate-100 relative max-w-[160px]">
        <StyleThumbnail
          src={style.thumbnail}
          updatedAt={style.updatedAt}
          alt={localizeName(style)}
          className="w-full h-full"
        />
        {/* Selected indicator */}
        {isSelected && (
          <div className="absolute top-1.5 right-1.5 w-5 h-5 rounded-full bg-primary flex items-center justify-center shadow-sm">
            <Check className="w-3 h-3 text-white" />
          </div>
        )}
      </div>
      {/* Info section */}
      <div className="p-2">
        <h3 className="font-semibold text-xs text-slate-900 mb-0.5">
          {localizeName(style)}
        </h3>
        <p className="text-[13px] text-slate-600 leading-snug line-clamp-2">
          {localizeDescription(style)}
        </p>
      </div>
    </button>
  )
}
